import cloneDeep from 'lodash.clonedeep';
import {
  FormTemplate, FormTemplateField, FormTemplateSection, FormTemplateSectionMetadataLayout,
} from 'data/template/template.types';
import { TargetDeliveryTemplateAccessFunctionFormData, TargetDeliveryTemplateInterfaceFormData } from 'data/targetDeliveryTemplateAccessFunction/targetDeliveryTemplateAccessFunction.types';
import { GENBAND_TARGET_CCC_ROWS } from '../templates/target-delivery-genband-20-template';
import { TargetTemplateFormSection } from '../TargetDeliveryTemplateAccessFunction.types';

// eslint-disable-next-line import/prefer-default-export
export const adaptTemplateBasedOnSections = (
  template: FormTemplate | undefined,
  fieldName: string,
  value: string | boolean,
  formSection: TargetTemplateFormSection,
): FormTemplate | undefined => {

  if (template == null) {
    return;
  }

  const clonedTemplate = cloneDeep(template);
  clonedTemplate.formId = Date.now().toString();

  if (formSection === 'targetInformation' && template.key === 'GENBANDC20') {
    adaptTemplateBasedOnACC(
      clonedTemplate.targetInformation as FormTemplateSection,
      clonedTemplate.callContentChannels as FormTemplateSection,
      fieldName,
      value as string,
    );
  }

  if (formSection === 'callContentChannels') {
    if (fieldName === 'SANREQ') {
      enableDisableFiled(clonedTemplate, formSection, 'SAN', value === 'false');
    }

    if (fieldName === 'PREPENDRTEDIGS') {
      enableDisableFiled(clonedTemplate, formSection, 'NUMRTEDIGS', value === 'false');
    }
  }

  return clonedTemplate;
};

function enableField(
  field: FormTemplateField,
  shouldEnable: boolean,
): void {

  if (shouldEnable) {
    field.disabled = false;
  }
}

function disableField(
  field: FormTemplateField,
): void {
  field.disabled = true;
  field.initial = false;
}

export function updateTemplateFieldsValues(template: FormTemplate, targetType: string): void {
  updateNetworkTypeBaseOnTargetType(template, targetType);
  updateTargetInfoBaseOnTargetType(template, targetType);

  const netWorkType: FormTemplateSection = template.networkType as FormTemplateSection;
  if (netWorkType?.fields.GSM?.initial
    || netWorkType?.fields.FIX?.initial
    || netWorkType?.fields.POC?.initial
    || netWorkType?.fields.NGN?.initial
    || netWorkType?.fields.IMS?.initial
    || netWorkType?.fields.HLR?.initial) {
    enableField((template.callContentType as FormTemplateSection).fields.Voice, true);
  } else {
    disableField((template.callContentType as FormTemplateSection).fields.Voice);
  }

  enableField((template.callContentType as FormTemplateSection).fields.DATA, true);

  if (netWorkType?.fields.GSM?.initial
    || netWorkType?.fields.LTE?.initial
    || netWorkType?.fields.NGN?.initial
    || netWorkType?.fields.IMS?.initial
    || netWorkType?.fields.VOIP?.initial) {
    enableField((template.callContentType as FormTemplateSection).fields.MM, true);
  } else {
    disableField((template.callContentType as FormTemplateSection).fields.MM);
  }

  if (netWorkType?.fields.GPRS?.initial
    || netWorkType?.fields.LTE?.initial) {
    enableField((template.callContentType as FormTemplateSection).fields.IRIPO, true);
  } else {
    disableField((template.callContentType as FormTemplateSection).fields.IRIPO);
  }

  if (netWorkType?.fields.GSM?.initial
    || netWorkType?.fields.VOIP?.initial
    || netWorkType?.fields.NGN?.initial
    || netWorkType?.fields.IMS?.initial) {
    enableField((template.callContentType as FormTemplateSection).fields.IRIMM, true);
  } else {
    disableField((template.callContentType as FormTemplateSection).fields.IRIMM);
  }

  if (netWorkType?.fields.IA?.initial) {
    enableField((template.callContentType as FormTemplateSection).fields.IA, true);
  } else {
    disableField((template.callContentType as FormTemplateSection).fields.IA);
  }

  template.formId = Date.now().toString();
}

function updateNetworkTypeBaseOnTargetType(template: FormTemplate, targetType: string | undefined) {
  if (targetType === 'MSISDN' || targetType === 'IMSI' || targetType === 'IMEI' || targetType === 'SIP-URI') {
    disableField((template.networkType as FormTemplateSection).fields.IA);
  } else {
    enableField((template.networkType as FormTemplateSection).fields.IA, true);
  }

  if (targetType === 'SIP-URI' || targetType === 'PSTN') {
    disableField((template.networkType as FormTemplateSection).fields.VOIP);
  } else {
    enableField((template.networkType as FormTemplateSection).fields.VOIP, true);
  }

  if (targetType === 'IMSI' || targetType === 'IMEI') {
    disableField((template.networkType as FormTemplateSection).fields.NGN);
  } else {
    enableField((template.networkType as FormTemplateSection).fields.NGN, true);
  }

  if (targetType === 'IMEI' || targetType === 'TEL-URI' || targetType === 'PSTN') {
    disableField((template.networkType as FormTemplateSection).fields.HLR);
  } else {
    enableField((template.networkType as FormTemplateSection).fields.HLR, true);
  }

  if (targetType === 'IMEI' || targetType === 'PSTN') {
    disableField((template.networkType as FormTemplateSection).fields.POC);
  } else {
    enableField((template.networkType as FormTemplateSection).fields.POC, true);
  }

  if (targetType === 'SIP-URI' || targetType === 'TEL-URI') {
    disableField((template.networkType as FormTemplateSection).fields.GSM);
    disableField((template.networkType as FormTemplateSection).fields.GPRS);
  } else {
    enableField((template.networkType as FormTemplateSection).fields.GSM, true);
    enableField((template.networkType as FormTemplateSection).fields.GPRS, true);
  }

  if (targetType === 'PSTN') {
    disableField((template.networkType as FormTemplateSection).fields.IA);
    disableField((template.networkType as FormTemplateSection).fields.GPRS);
    disableField((template.networkType as FormTemplateSection).fields.LTE);
  } else {
    enableField((template.networkType as FormTemplateSection).fields.IA, true);
    enableField((template.networkType as FormTemplateSection).fields.GPRS, true);
    enableField((template.networkType as FormTemplateSection).fields.LTE, true);
  }
}

function updateTargetInfoBaseOnTargetType(template: FormTemplate, targetType: string | undefined) {
  const targetInfoSection = (template.targetInformation as FormTemplateSection)
  if (targetType === 'PSTN') {
    targetInfoSection.fields.DIR.hide = 'false';
    targetInfoSection.fields.TON.hide = 'false';
  } else {
    targetInfoSection.fields.DIR.hide = 'true';
    targetInfoSection.fields.TON.hide = 'true';
  }
}

const enableDisableFiled = (
  template: FormTemplate,
  formSection: 'targetInformation' | 'callContentChannels',
  field: string,
  disabled: boolean,
) => {
  (template[formSection] as FormTemplateSection).fields[field].disabled = disabled;
};

const adaptTemplateBasedOnACC = (
  targetInformationSection: FormTemplateSection,
  callContentChannelsSection: FormTemplateSection,
  fieldName: string,
  value: string,
): void => {

  if (fieldName === 'ACC') {
    showFieldbasedOnACC(targetInformationSection, value);
    (targetInformationSection.metadata.layout as FormTemplateSectionMetadataLayout).rows = updateRows(value, 'targetInformation');
    (callContentChannelsSection.metadata.layout as FormTemplateSectionMetadataLayout).rows = updateRows(value, 'callContentChannels');
  }

};

function updateRows(
  targetInformationAAA: string,
  section: 'targetInformation' | 'callContentChannels',
): string[][] {

  const key = targetInformationAAA as ('IBN' | 'BRI' | 'PRI' | 'MFT' | 'VA' | 'HNT' | 'TRK');

  const newRows: string[][] = GENBAND_TARGET_CCC_ROWS?.[section]?.[key];

  if (newRows == null) {
    return [[]];
  }

  return newRows;
}

const showFieldbasedOnACC = (templateSection: FormTemplateSection, value: string) => {
  showField(templateSection, 'VARIANT', value === 'PRI' || value === 'TRK');
  showField(templateSection, 'CLLI', value === 'PRI');
  showField(templateSection, 'FDCP', value === 'PRI');
  showField(templateSection, 'CGNDN', value === 'PRI');
  showField(templateSection, 'CDNDN', value === 'PRI');
  showField(templateSection, 'LTID', value === 'BRI' || value === 'PRI');
  showField(templateSection, 'LEN', value === 'HNT');
};

const showField = (templateSection: FormTemplateSection, field: string, show: boolean) => {
  templateSection.fields[field].hide = show ? 'false' : 'true';
};

export const adaptInterfacemSectionFormData = (
  interfaceFormDataList: TargetDeliveryTemplateInterfaceFormData[] | undefined,
  interfaceId: string | undefined,
  fieldName: keyof TargetDeliveryTemplateInterfaceFormData,
  value: unknown,
): TargetDeliveryTemplateInterfaceFormData[] | undefined => {
  const clonedInterfaceFormDataList = cloneDeep(interfaceFormDataList);

  return clonedInterfaceFormDataList?.map((interfaceFormData) => {
    if (interfaceFormData.id === interfaceId) {
      return handleInterfaceFieldUpdate(interfaceFormData, fieldName, value);
    }

    return interfaceFormData;
  });
};

function handleInterfaceFieldUpdate(
  interfaceFormData: TargetDeliveryTemplateInterfaceFormData,
  fieldName: keyof TargetDeliveryTemplateInterfaceFormData | string,
  fieldValue: unknown,
) {
  setInterfaceFieldValue(interfaceFormData, fieldName, fieldValue);
  return interfaceFormData;
}

function setInterfaceFieldValue(
  interfaceFormData: any,
  fieldName: string,
  value: unknown,
): TargetDeliveryTemplateInterfaceFormData {
  interfaceFormData[fieldName] = value;
  return interfaceFormData;
}
const NETYPE_SECTIONS_TO_DISPLAY = {
  GPRS: ['EIMSTARGET', 'EIMSGPRSTRGT', 'EIMSMCNB'],
  HLR: ['EIMSTARGET'],
  MSC: ['EIMSTARGET', 'EIMSMSCTRGT', 'EIMSMCNB'],
  FIX: ['EIMSTARGET', 'EIMSWRLNTRGT', 'EIMSMCNB'],
  SIPSERVER: ['EIMSTARGET', 'EIMSMCNB'],
  OV_SIPSERVER: ['EIMSTARGET', 'EIMSMC'],
  OV_MOWLAN: ['EIMSTARGET', 'EIMSMC'],
  OV_GPRS: ['EIMSTARGET', 'EIMSMC'],
} as { [key: string]: string[] };

export const adaptTemplateForNEType = (
  formTemplate: FormTemplate,
  neType: string
) => {
  ['EIMSTARGET', 'EIMSGPRSTRGT', 'EIMSMSCTRGT', 'EIMSMCNB', 'EIMSWRLNTRGT', 'EIMSMC'].forEach(
    (formSection) => {
      if (formTemplate[formSection]) {
        (formTemplate[
          formSection
        ] as FormTemplateSection).metadata.hide = 'true';
      }
    }
  );
  NETYPE_SECTIONS_TO_DISPLAY[neType].forEach((formSection) => {
    if (formTemplate[formSection]) {
      (formTemplate[
        formSection
      ] as FormTemplateSection).metadata.hide = 'false';
    }
  });
  if (neType === 'MSC') {
    (formTemplate.EIMSMCNB as FormTemplateSection).fields.MCNBTYPE.initial = 'SINGLE';
    (formTemplate.EIMSMCNB as FormTemplateSection).fields.MCNBTYPE.options = [
      {
        label: 'SINGLE',
        value: 'SINGLE'
      },
      {
        label: 'AVFNB',
        value: 'AVFNB'
      }
    ];
  } else if (neType === 'FIX') {
    (formTemplate.EIMSMCNB as FormTemplateSection).fields.MCNBTYPE.initial = 'SINGLE';
    (formTemplate.EIMSMCNB as FormTemplateSection).fields.MCNBTYPE.options = [
      {
        label: 'SINGLE',
        value: 'SINGLE'
      },
      {
        label: 'MULTIPLE',
        value: 'MULTIPLE'
      },
      {
        label: 'DIVERTED',
        value: 'DIVERTED'
      }
    ];
  } else {
    (formTemplate.EIMSMCNB as FormTemplateSection).fields.MCNBTYPE.initial = 'IP';
    (formTemplate.EIMSMCNB as FormTemplateSection).fields.MCNBTYPE.options = [
      {
        label: 'IP',
        value: 'IP'
      },
      {
        label: 'PDHIR',
        value: 'PDHIR'
      }
    ];
  }
  (formTemplate as FormTemplate).formId = Date.now().toString();
  return formTemplate;
};

export const adaptFormDataForNEType = (
  formTemplate: FormTemplate,
  interfaceFormData: Partial<TargetDeliveryTemplateAccessFunctionFormData>
): Partial<TargetDeliveryTemplateAccessFunctionFormData> => {
  if (!interfaceFormData || !interfaceFormData.EIMSMCNB) {
    return interfaceFormData;
  }
  const clonedInterfaceFormData = cloneDeep(interfaceFormData);
  const value = (formTemplate.EIMSMCNB as FormTemplateSection).fields.MCNBTYPE.initial;
  const EIMSMCNBFormData = clonedInterfaceFormData.EIMSMCNB;
  clonedInterfaceFormData.EIMSMCNB = EIMSMCNBFormData?.map((interfaceFormData) => {
    return handleInterfaceFieldUpdate(interfaceFormData, 'MCNBTYPE', value);
  });

  return clonedInterfaceFormData;
};
